import React from 'react';
import { useSelector, useDispatch } from 'react-redux';

import BillingConstants from 'app/shared/constants/BillingConstants';
import AnalyticsConstants from 'app/shared/constants/AnalyticsConstants';

import analyticsUtils from 'app/client/utils/analyticsUtils';

import BillingActions from 'app/shared/actions/BillingActions';

import LoadingConstants from 'app/shared/constants/LoadingConstants';
import ErrorMessageConstants from 'app/shared/constants/ErrorMessageConstants';

import SpinnerContainer from 'app/shared/components/SpinnerContainer';

import { Alert } from '@zillow/constellation';

import { BillingAddPaymentModalContainer, StyledSpinnerContainer } from './StyledComponents';

const { PAYMENT_TYPES } = BillingConstants;

const PAYMENT_METHOD_MAPPINGS = {
    [PAYMENT_TYPES.ACH]: 'ACH:Zillow:Zillow Rental Network',
    [PAYMENT_TYPES.CREDIT_CARD]: 'CC:Zillow:Zillow Rental Network',
};

let PaymentMethodIframeProviderWrapper;

const MetroidIframeWrapper = ({
    accountNumber,
    onCreditCardAdded,
    selectedType,
}) => {
    const dispatch = useDispatch();
    const user = useSelector(state => state.user);
    const paymentSessionInfo = useSelector(state => state.billing.payment.paymentSessionInfo);
    const paymentSessionInfoLoadingState = useSelector(state => state.billing.payment.paymentSessionInfoLoadingState);

    const metroidHPPConfigId = PAYMENT_METHOD_MAPPINGS[selectedType];
    const [isClient, setIsClient] = React.useState(false);
    const [iframeRendered, setIframeRendered] = React.useState(false);
    const iframeRef = React.useRef(null);
    const timer = React.useRef(null);
    const [customFields, setCustomFields] = React.useState(null);
    const [metroidAccountId, setMetroidAccountId] = React.useState(null);
    const [sessionToken, setSessionToken] = React.useState(null);
    const [errorMessage, setErrorMessage] = React.useState(null);

    const pollForIframe = () => {
        timer.current = setTimeout(() => {
            if (iframeRef.current.clientHeight > 0) {
                setIframeRendered(true);
            }
            pollForIframe();
        }, 200);
    };

    React.useEffect(() => {
        dispatch(BillingActions.getPaymentMethodSessionForAccount(accountNumber));
        if (__CLIENT__) {
            setIsClient(__CLIENT__);
            PaymentMethodIframeProviderWrapper = require('@zillow/metroid-payment-method').default;
        }
        pollForIframe();

        if (selectedType === PAYMENT_TYPES.ACH) {
            dispatch(analyticsUtils.track.GAEventV2(AnalyticsConstants.GA_EVENTS.BILLING_PORTAL_ADD_ACH));
        } else if (selectedType === PAYMENT_TYPES.CREDIT_CARD) {
            dispatch(analyticsUtils.track.GAEventV2(AnalyticsConstants.GA_EVENTS.BILLING_PORTAL_ADD_CREDIT_CARD));
        }
        return () => clearTimeout(timer.current);
    }, []);

    React.useEffect(() => {
        setMetroidAccountId({
            brand: paymentSessionInfo.brand,
            businessUnit: paymentSessionInfo.businessUnit,
            userId: paymentSessionInfo.userId,
        });
        setSessionToken({
            referenceKey: paymentSessionInfo.referenceKey,
            secret: paymentSessionInfo.secret,
        });

        setCustomFields({
            ExternalUserId__c: user.userToken,
        });
    }, [paymentSessionInfo]);

    const handleSubmitFail = (e) => {
        console.error(e);
        setErrorMessage('Failed to load payment methods');
    };

    if (isClient) {
        const showIframe = paymentSessionInfoLoadingState === LoadingConstants.SUCCESS
            && metroidAccountId && metroidAccountId && sessionToken;

        const showSpinner = (paymentSessionInfoLoadingState === LoadingConstants.LOADING
            || paymentSessionInfoLoadingState === LoadingConstants.INITIAL) || (!iframeRendered && !errorMessage);

        return (
            <BillingAddPaymentModalContainer>
                {showSpinner && (
                    <StyledSpinnerContainer>
                        <SpinnerContainer />
                    </StyledSpinnerContainer>
                )}
                {(errorMessage || paymentSessionInfoLoadingState === LoadingConstants.ERROR) && (
                    <Alert
                        appearance="error"
                        body={errorMessage || ErrorMessageConstants.DEFAULT_ERROR}
                    />
                )}
                <div ref={iframeRef} style={{ display: iframeRendered ? 'block' : 'hidden' }}>
                    {showIframe && (
                        <PaymentMethodIframeProviderWrapper
                            className="my-credit-card"
                            onSubmitSuccess={onCreditCardAdded}
                            onSubmitFailure={handleSubmitFail}
                            sessionToken={sessionToken}
                            metroidAccountId={metroidAccountId}
                            metroidConfigUrl={paymentSessionInfo.metroidConfigUrl}
                            metroidHPPConfigId={metroidHPPConfigId}
                            endpointVersion="v1"
                            testConfigurations={{}}
                            setAsDefault={false}
                            customFields={customFields}
                            onRenderComplete={() => { }}
                        />
                    )}
                </div>
            </BillingAddPaymentModalContainer>
        );
    }

    return null;
};

export default MetroidIframeWrapper;
